/**
 * Stage 7: Image Generation — context-based version using StagePanel.
 *
 * Two-step: generate image description (LLM), then generate image (Imagen).
 * Custom UI for prompt editing, style/reference selection, and image preview.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { StagePanel } from '../StagePanel';
import { useArticleContext } from '../ArticleContext';
import { jobsService } from '../../../services/jobsService';
import { blogRunsService } from '../../../services/blogRunsService';
import { skillsService } from '../../../services/skillsService';
import type { ArticleDocument } from '../types';

function ImageGrid({ s3Keys, selectedIdx, onSelect }: { s3Keys: string[]; selectedIdx: number | null; onSelect: (i: number) => void }) {
  const [urls, setUrls] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      const newUrls: Record<number, string> = {};
      for (let i = 0; i < s3Keys.length; i++) {
        try {
          const url = await skillsService.presignView(s3Keys[i]);
          if (!cancelled) newUrls[i] = url;
        } catch {
          // presign failed for this key
        }
      }
      if (!cancelled) {
        setUrls(newUrls);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [s3Keys]);

  return (
    <div>
      <div className="text-xs font-medium text-gray-700 mb-1">
        Generated Images ({s3Keys.length}) — click to select
      </div>
      <div className="grid grid-cols-2 gap-2">
        {s3Keys.map((key, i) => (
          <button
            key={i}
            type="button"
            className={`border-2 rounded overflow-hidden ${
              selectedIdx === i ? 'border-blue-500' : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => onSelect(i)}
          >
            {urls[i] ? (
              <img src={urls[i]} alt={`Generated image ${i + 1}`} className="w-full" />
            ) : loading ? (
              <div className="h-40 flex items-center justify-center text-xs text-gray-400">Loading image...</div>
            ) : (
              <div className="h-40 flex items-center justify-center text-xs text-red-400">Failed to load</div>
            )}
            <div className="text-[10px] text-center py-1">
              Image {i + 1} {selectedIdx === i && <span className="text-blue-600 font-medium">— Selected</span>}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function ImageControls() {
  const { document, run, refreshDocument, refreshArticles } = useArticleContext();
  const image = document?.article?.featured_image;
  const [description, setDescription] = useState(image?.description || '');
  const [styleDescription, setStyleDescription] = useState(image?.style_description || '');
  const [aspectRatio, setAspectRatio] = useState(image?.aspect_ratio || '16:9');
  const [generatingDescription, setGeneratingDescription] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState<number | null>(image?.selected_index ?? null);
  const [stageError, setStageError] = useState<string | null>(null);
  const [defaultsLoaded, setDefaultsLoaded] = useState(false);

  // Load default style from pipeline defaults if not already set
  React.useEffect(() => {
    if (defaultsLoaded || styleDescription) return;
    blogRunsService.getPipelineDefaults()
      .then((defaults) => {
        if (!styleDescription && defaults.default_image_style) {
          setStyleDescription(defaults.default_image_style);
        }
        setDefaultsLoaded(true);
      })
      .catch(() => setDefaultsLoaded(true));
  }, [defaultsLoaded, styleDescription]);

  // Sync local state when document updates
  React.useEffect(() => {
    if (image?.description && !description) {
      setDescription(image.description);
    }
    if (image?.style_description && !styleDescription) {
      setStyleDescription(image.style_description);
    }
    if (image?.s3_keys && image.s3_keys.length > 0 && selectedIdx == null) {
      setSelectedIdx(image.selected_index ?? null);
    }
  }, [image?.description, image?.style_description, image?.s3_keys, image?.selected_index]);

  const generateDescription = useCallback(async () => {
    if (!run?.run_id) return;
    setGeneratingDescription(true);
    setStageError(null);
    try {
      const { job_id } = await jobsService.submit('blog_stage_execute', {
        run_id: run.run_id,
        stage_id: 'stage_7_image_generation',
        description: '',
        description_only: true,
        style_description: styleDescription,
      });
      const finalJob = await jobsService.waitForCompletion(job_id);
      await refreshDocument();
      if (finalJob.status === 'failed') {
        setStageError((finalJob as any).error || 'Description generation failed');
      } else if (finalJob.status === 'succeeded') {
        await refreshArticles();
      }
    } catch (e: any) {
      setStageError(e?.message || 'Description generation failed');
    } finally {
      setGeneratingDescription(false);
    }
  }, [run, styleDescription, refreshDocument, refreshArticles]);

  const generateImage = useCallback(async () => {
    if (!run?.run_id || !description.trim()) return;
    setGeneratingImage(true);
    try {
      // Pass description and style separately — backend assembles the prompt
      const { job_id } = await jobsService.submit('blog_stage_execute', {
        run_id: run.run_id,
        stage_id: 'stage_7_image_generation',
        description: description,
        style_description: styleDescription,
        aspect_ratio: aspectRatio,
      });
      const finalJob = await jobsService.waitForCompletion(job_id);
      await refreshDocument();
      if (finalJob.status === 'succeeded') {
        await refreshArticles();
      }
    } catch (e: any) {
      console.error('Image generation failed:', e);
    } finally {
      setGeneratingImage(false);
    }
  }, [run, description, styleDescription, aspectRatio, refreshDocument, refreshArticles]);

  const saveSelection = useCallback(async () => {
    if (!run?.run_id || selectedIdx == null) return;
    await blogRunsService.updateDocument(run.run_id, {
      article: {
        featured_image: {
          selected_index: selectedIdx,
        },
      },
    });
    await refreshDocument();
    await refreshArticles();
  }, [run, selectedIdx, refreshDocument, refreshArticles]);

  return (
    <div className="space-y-4">
      {/* Step 1: Object Description */}
      <div>
        <div className="text-xs font-medium text-gray-700 mb-1">Object Description</div>
        <p className="text-xs text-gray-500 mb-2">What the image depicts. Click Generate to create one from the article title and keyword.</p>
        <div className="flex items-center gap-2 mb-2">
          <button
            type="button"
            className="px-3 py-1.5 text-xs rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50 border border-gray-300"
            disabled={generatingDescription}
            onClick={generateDescription}
          >
            {generatingDescription ? 'Generating...' : 'Generate Description'}
          </button>
        </div>
        {stageError && (
          <div className="mb-2 bg-red-50 border border-red-200 rounded px-3 py-2 text-sm text-red-700">{stageError}</div>
        )}
        <textarea
          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="A climbing plant growing through stacked books and a lightbulb..."
        />
      </div>

      {/* Step 2: Style Description */}
      <div>
        <div className="text-xs font-medium text-gray-700 mb-1">Visual Style</div>
        <p className="text-xs text-gray-500 mb-2">How the image should look. Pre-populated with brand defaults.</p>
        <textarea
          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
          rows={3}
          value={styleDescription}
          onChange={(e) => setStyleDescription(e.target.value)}
          placeholder="Centered composition on a dark navy-black background..."
        />
      </div>

      {/* Step 3: Generate Image */}
      <div>
        <div className="text-xs font-medium text-gray-700 mb-1">Generate Image</div>
        <div className="flex items-center gap-3">
          <select
            className="border border-gray-300 rounded px-2 py-1 text-sm"
            value={aspectRatio}
            onChange={(e) => setAspectRatio(e.target.value)}
          >
            <option value="16:9">16:9</option>
            <option value="4:3">4:3</option>
            <option value="1:1">1:1</option>
            <option value="3:4">3:4</option>
            <option value="9:16">9:16</option>
          </select>
          <button
            type="button"
            className="px-3 py-1.5 text-sm rounded-md text-white hover:opacity-90 disabled:opacity-50" style={{ backgroundColor: '#1a2332' }}
            disabled={generatingImage || !description.trim()}
            onClick={generateImage}
          >
            {generatingImage ? 'Generating...' : 'Generate Image'}
          </button>
        </div>
      </div>

      {/* Generated images */}
      {image?.s3_keys && image.s3_keys.length > 0 && (
        <div>
          <ImageGrid
            s3Keys={image.s3_keys}
            selectedIdx={selectedIdx}
            onSelect={setSelectedIdx}
          />
          {selectedIdx != null && (
            <button
              type="button"
              className="mt-2 px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
              onClick={saveSelection}
            >
              Save Image {selectedIdx + 1} as Featured
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function renderOutput(doc: ArticleDocument) {
  const image = doc.article?.featured_image;
  if (!image) {
    return <div className="text-sm text-gray-500">No featured image yet.</div>;
  }
  return (
    <div className="text-xs text-gray-600 space-y-1">
      {image.description && <div><span className="text-gray-400">Description:</span> {image.description}</div>}
      <div><span className="text-gray-400">Images:</span> {(image.s3_keys || []).length} generated</div>
      <div><span className="text-gray-400">Selected:</span> {image.selected_index != null ? `Image ${image.selected_index + 1}` : 'None'}</div>
      {image.aspect_ratio && <div><span className="text-gray-400">Aspect ratio:</span> {image.aspect_ratio}</div>}
    </div>
  );
}

export function Stage7ImageV2() {
  return (
    <StagePanel
      stageId="stage_7_image_generation"
      title="Stage 7: Image Generation"
      description="Generate and select a featured image for the article."
      hideRunButton
      renderOutput={renderOutput}
    >
      <ImageControls />
    </StagePanel>
  );
}
